import { useEffect, useState } from "react";

import {
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";

import { UserService } from "~/services";
import { useKeycloak } from "../providers/KeycloakProvider";

export const useLegalAgreements = () => {
  const keycloak = useKeycloak();

  const [isUserAgreed, setIsUserAgreed] = useState<null | boolean>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      terms: false,
      privacy: false,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const watchedValues = useWatch({ control });

  const onSubmit = async () => {
    setHasError(false);
    try {
      setIsSaving(true);

      const response = await UserService.acceptUserLegalAgreements();
      if (response?.status === 204) {
        setIsUserAgreed(true);
        await UserService.getUserState();
      } else {
        throw new Error("Failed to accept legal agreements");
      }
    } catch {
      setHasError(true);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!keycloak.authenticated) {
      return;
    }

    setIsUserAgreed(!!keycloak.userState?.legalAgreementStatus?.agreedToLegalAgreements);
    setIsLoading(false);
  }, [keycloak]);

  if (!keycloak.authenticated || isLoading) {
    return undefined;
  }

  if (isUserAgreed === true) {
    return false;
  }

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <Dialog
        slotProps={{
          root: {
            sx: { position: "absolute" },
          },
          paper: {
            sx: { outline: "none", m: 0 },
          },
        }}
        disablePortal
        disableRestoreFocus
        hideBackdrop
        open
      >
        {!!isSaving && (
          <Box
            sx={(theme) => ({
              width: "100%",
              height: "100%",
              position: "absolute",
              alignContent: "center",
              textAlign: "center",
              background: alpha(theme.palette.background.paper, 0.6),
              zIndex: theme.zIndex.drawer - 1,
            })}
          >
            <CircularProgress size={150} />
            <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>Saving...</Typography>
          </Box>
        )}
        <DialogTitle align="center" color="custom.main" fontWeight="bold" variant="h4" gutterBottom>
          Updated Agreements
        </DialogTitle>
        <DialogContent>
          <Typography>
            We have updated our Terms and Conditions and Privacy Policy, please read and agree to them to continue using our services.
          </Typography>
          <Stack
            component="form"
            direction="column"
            flexGrow={1}
            height="100%"
            id="legal-agreements-form"
            onSubmit={handleSubmit(onSubmit)}
            pt={1}
            noValidate
          >
            <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="terms"
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    )}
                    rules={{ validate: (checked) => !!checked }}
                  />
                }
                label={
                  <span>
                    I agree to the{" "}
                    <Link href={`${window.location.origin}/terms-and-conditions.htm`} rel="noopener noreferrer" target="_blank">
                      Terms and Conditions
                    </Link>
                  </span>
                }
                slotProps={{
                  typography: {
                    variant: "body2",
                  },
                }}
                required
              />
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="privacy"
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    )}
                    rules={{ validate: (checked) => !!checked }}
                  />
                }
                label={
                  <span>
                    I agree to the{" "}
                    <Link href={`${window.location.origin}/privacy-policy.htm`} rel="noopener noreferrer" target="_blank">
                      Privacy Policy
                    </Link>
                  </span>
                }
                slotProps={{
                  typography: {
                    variant: "body2",
                  },
                }}
                required
              />
            </FormGroup>
            {!!hasError && (
              <Typography color="error" sx={{ mt: 2 }}>
                Error saving, please try again or contact customer service
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={!watchedValues.privacy || !watchedValues.terms} form="legal-agreements-form" type="submit">
            {hasError ? "Try Again" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
